/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import { pluck, pluckNumber, getValidValue, getDashStyle, getFirstColor,
  parseUnsafeString, preDefStr, toRaphaelColor } from '@fusioncharts/core/src/lib';
import Column from '@fusioncharts/charts/src/dataset/column';
import { _checkPointerOverPlot, _checkPointerOverErrorBar } from '../../dataset/errorbar2d';
import { getPlotIndices, _firePlotEvent, _parseToolText as __parseToolText } from './candlestick';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import candlestickbarAnimation from './candlestickbar.animation';

let colorStrings = preDefStr.colors,
  COLOR_B90000 = colorStrings.B90000,
  COLOR_FFFFFF = colorStrings.FFFFFF,
  SHOWSHADOW = 'showShadow',
  ROUND = preDefStr.ROUND,
  visibleStr = preDefStr.visibleStr,
  DASH_DEF = 'none',
  POINTER = 'pointer',
  BLANKSTRING = '',
  M = 'M',
  L = 'L',
  applyShadow = function (shadow) {
    return function () {
      this.shadow(shadow);
    };
  };

addDep({
  name: 'candlestickbarAnimation',
  type: 'animationRule',
  extension: candlestickbarAnimation
});
/**
 * Class def for bull stick chart API
 * This dataset is reponsible for drawing stick depending on high and open value
 */
class CandleStickBar extends Column {
  /**
   * function to Remove data plots if the number of current data plots/categories
   * is more than the existing ones.
   * @param  {Object} datasetJSON JSON for dataset configurations
   */
  trimData (datasetJSON) {
    if (!this.components && this.components.data && this.components.data.length) {
      return;
    }
    let comps = this.components,
      prevData = comps && comps.data,
      prevDataLength = prevData && prevData.length, // eslint-disable-line good-practices/no-single-usage-variable
      data = datasetJSON.data,
      currDataLength = (Array.isArray(data) && data.filter(v => (v.high || v.open || v.close || v.low)).length) || 0, // eslint-disable-line good-practices/no-function-dependency
      dataDiff = prevDataLength - currDataLength;

    // Removing data plots if the number of current data plots
    // is less than the existing ones.
    if (dataDiff > 0) {
      this.removeData(currDataLength, dataDiff, false);
    }
  }
  /**
   * Adds dataset Specific event Arguments
   * @param {Object} _dataObj dataObject
   */
  // eslint-disable-next-line class-methods-use-this
  addDatasetSpecificEvtArgs (_dataObj) {
    let dataObj = _dataObj,
      config = dataObj.config,
      trackerConfig = dataObj.trackerConfig,
      eventArgs = trackerConfig.eventArgs;
    Object.assign(eventArgs, {
      open: config.open,
      close: config.close,
      high: config.high,
      low: config.low,
      volume: config.volume,
      alpha: config.alpha,
      x: config.x,
      displayValue: config.displayValue,
      color: config.color,
      borderColor: config.borderColor,
      dashed: config.dashed
    });
  }
  /**
    * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
    * This function is called once from the init() function of the Column class.
    * @param  {Object} datasetJSON JSON for dataset configurations
    * @return {boolean} Indicates whether the configuration was successful or not
    */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.config.JSONData = datasetJSON;

    var dataset = this,
      conf = dataset.config,
      chart = dataset.getFromEnv('chart'),
      rawDataObj = chart.getFromEnv('dataSource'), // eslint-disable-line good-practices/no-single-usage-variable
      JSONData = dataset.config.JSONData,
      dataArr = JSONData.data || [],
      chartAttr = rawDataObj.chart,
      dataLength = dataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      numberFormatter = dataset.getFromEnv('number-formatter'),
      colorM = dataset.getFromEnv('color-manager'),
      setColor,
      setBorderColor,
      setAlpha,
      // Candle stick properties.
      // Bear fill and border color - (Close lower than open)
      bearBorderColor = conf.bearBorderColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearbordercolor, COLOR_B90000)),
      bearFillColor = conf.bearFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearfillcolor, COLOR_B90000)),
      // Bull fill and border color - Close higher than open
      bullBorderColor = conf.bullBorderColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bullbordercolor,
                      colorM.getColor('canvasBorderColor'))),
      bullFillColor = conf.bullFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                getFirstColor(pluck(chartAttr.bullfillcolor,
                  COLOR_FFFFFF)),
      index,
      dataObj,
      open,
      close,
      high,
      low,
      minValue,
      maxValue,
      x,
      dataStore,
      setData,
      config,
      isCandleStick = false, // eslint-disable-line good-practices/no-single-usage-variable
      yMax = -Infinity,
      yMin = +Infinity,
      xMax = -Infinity,
      xMin = +Infinity,
      userMaxColWidth;

    dataset.setState('visible', pluckNumber(JSONData.visible, 1) === 1);

    dataset._conatinerHidden = !!dataset.getState('visible');

    conf.defaultPadding = {
      left: 0.5,
      right: 0.5
    };
    conf.minAbsNonZeroValue = Infinity;
    conf.minAbsNonZeroData = {};
    conf.linethickness = pluckNumber(chartAttr.plotlinethickness, 2);
    conf.toolText = getValidValue(parseUnsafeString(pluck(JSONData.tooltext, chartAttr.plottooltext), false));
    // Dataset seriesname
    conf.name = getValidValue(JSONData.seriesname);
    conf.showTooltip = pluckNumber(chartAttr.showtooltip, 1);

    conf.showErrorValue = true;
    conf.errorBarWidthPercent = 0;
    isCandleStick = true; // eslint-disable-line good-practices/no-single-usage-variable
    userMaxColWidth = pluck(chartAttr.maxcolwidth); // eslint-disable-line good-practices/no-single-usage-variable
    conf.maxColWidth = Math.abs(pluckNumber(
      userMaxColWidth, 50)) || 1;
    // Animation related attributes configuration
    conf.enableAnimation = pluckNumber(chartAttr.animation,
      chartAttr.defaultanimation, 1);
    conf.animation = !conf.enableAnimation ? false : {
      duration: pluckNumber(chartAttr.animationduration, 1) * 1000
    };

    conf.plotLineDashLen = pluckNumber(chartAttr.plotlinedashlen, 5);
    conf.plotLineDashGap = pluckNumber(chartAttr.plotlinedashgap, 4);
    dataStore = dataset.components.data = dataset.components.data || (dataset.components.data = []);
    conf.valuePadding = pluckNumber(JSONData.valuepadding, chartAttr.valuepadding, 2);
    conf.showShadow = pluckNumber(chartAttr.showshadow, colorM.getColor(SHOWSHADOW));
    // Iterate through all level data
    for (index = 0; index < dataLength; index++) {
      // Individual data obj
      // for further manipulation
      setData = dataArr[index];
      dataObj = dataStore[index];
      if (!dataObj) {
        dataObj = dataStore[index] = {};
      }
      !dataObj.config && (dataObj.config = {});
      !dataObj.graphics && (dataObj.graphics = {});
      config = dataObj.config;
      if (setData && !setData.vline) {
        config.setLink = pluck(setData.link);
        open = config.open = numberFormatter.getCleanValue(setData.open);
        close = config.close = numberFormatter.getCleanValue(setData.close);
        high = numberFormatter.getCleanValue(setData.high);
        low = numberFormatter.getCleanValue(setData.low);
        config.volume = numberFormatter.getCleanValue(setData.volume, true);
        x = config.x = numberFormatter.getCleanValue(setData.x);
        config.high = Math.max(open, close, high, low);
        config.low = Math.min(open, close, high, low);
        config.yVal = Math.max(open, close);
        minValue = Math.min(open, close, high, low);
        maxValue = Math.max(open, close, high, low);

        config.valuePadding = pluckNumber(setData.valuepadding, conf.valuePadding);

        // valueText = parseUnsafeString(getValidValue(setData.valuetext, BLANK));

        setBorderColor = getFirstColor(pluck(setData.bordercolor, close < open ? bearBorderColor
          : bullBorderColor));

        setAlpha = pluckNumber(setData.alpha, 100);
        setColor = getFirstColor(pluck(setData.color, close < open ? bearFillColor
          : bullFillColor));

        config.color = isCandleStick ? setColor : setBorderColor;
        config.alpha = setAlpha;
        /*
         * Storing the set level color and set level alpha which is required in drawing of line
         * In line we are checking setColor and setAlpha of current line segment is same as the previous
         * line segment's setColor and setAlpha by accesing config.setColor and config.setAlpha
         */
        config.setColor = config.color;
        config.setAlpha = config.alpha;
        // Finally add the data
        // we call getPointStub function that manage displayValue, toolText and link
        config.borderColor = setBorderColor;
        config.borderAlpha = config.plotLineAlpha;
        config.colorArr = [{
          color: config.color,
          alpha: config.alpha
        },
        {
          color: config.borderColor,
          alpha: config.borderAlpha
        }];
        config.showValue = 1;
        config.hoverEffects = {};
        config.link = pluck(setData.link);
        config.setValue = config.y;

        if (config.setValue !== 0 && conf.minAbsNonZeroValue > Math.abs(config.setValue)) {
          conf.minAbsNonZeroValue = Math.abs(config.setValue);
          conf.minAbsNonZeroData = config;
        }

        config.dataLabelStyle = dataset._configureDataLabelStyle(setData);

        if (minValue !== null) {
          yMax = Math.max(yMax, minValue);
          yMin = Math.min(yMin, minValue);
        }
        if (maxValue !== null) {
          yMax = Math.max(yMax, maxValue);
          yMin = Math.min(yMin, maxValue);
        }
        if (x !== null) {
          xMax = Math.max(xMax, x);
          xMin = Math.min(xMin, x);
        }
        config.dashStyle = pluckNumber(setData.dashed)
          ? getDashStyle(conf.plotLineDashLen, conf.plotLineDashGap) : DASH_DEF;
        config.shadow = {
          opacity: conf.showShadow ? (setAlpha / 100) : 0
        };
        x = (x !== null) ? x : index + 1;
        config.toolText = dataset._parseToolText(index);
        config.toolTipValue = BLANKSTRING;
        config._x = x;
        config.y = open;
        config.previousY = close;
        config.dashed = pluckNumber(conf.plotLineDashed, setData.dashed, 0);
      }
    }
    conf.yMax = yMax;
    conf.yMin = yMin;
    conf.xMax = xMax;
    conf.xMin = xMin;
    dataset.setState('dirty', true);
  }
  /**
   * functioto draw and update the graphics elements of plots
   * @param {Object} anim    - object reference of animation manager argument
   *                           consist of elements and their configuration
   */
  drawPlots () {
    var dataset = this,
      config,
      animationManager = dataset.getFromEnv('animationManager'),
      conf = dataset.config,
      data = dataset.components.data,
      length = data.length, // eslint-disable-line good-practices/no-single-usage-variable
      xAxis = dataset.getFromEnv('xAxis'), // eslint-disable-line good-practices/no-single-usage-variable
      yAxis = dataset.getFromEnv('yAxis'),
      // key,
      i,
      set,
      setLink,
      x,
      y,
      previousY,
      xPos,
      yPos,
      previousYPos,
      setElem,
      bars,
      trackerTolerance = conf.linethickness > 5 ? (conf.linethickness / 2) : 2.5,
      container = dataset.getContainer('plotGroup'),
      // hotElem,
      // targetGroup,
      highPos,
      lowPos,
      halfW,
      path,
      graphics;
    animationManager.setAnimation({
      el: container,
      attr: {
        opacity: dataset.getState('visible') ? 1 : 0
      },
      component: dataset
    });

    dataset.setColumnPosition();

    // draw plots
    for (i = 0; i < length; i += 1) {
      set = data[i];
      config = set.config;
      graphics = set.graphics;
      y = config.y;
      setElem = null;

      if (y === null) {
        setElem = graphics.element;
      } else {
        // when valid value
        x = config._x;
        setLink = config.link;
        config.setLink = config.link;
        // fixedWidth = config._FCW * groupMaxWidth;

        xPos = xAxis.getPixel(x);
        previousY = config.previousY;
        previousYPos = yAxis.getPixel(previousY);
        yPos = yAxis.getPixel(y);
        highPos = yAxis.getPixel(config.high);
        lowPos = yAxis.getPixel(config.low);
        halfW = dataset.getFromEnv('columnXShift');
        set.errorBar = [];
        bars = set.errorBar;
        bars.push([{
          _xPos: xPos - trackerTolerance,
          _yPos: highPos,
          _height: Math.abs(highPos - lowPos),
          _width: 2 * trackerTolerance
        },
        {
          _xPos: xPos + halfW,
          _yPos: yPos,
          _height: 2 * trackerTolerance,
          _width: Math.abs(halfW)
        },
        {
          _xPos: xPos,
          _yPos: previousYPos,
          _height: 2 * trackerTolerance,
          _width: Math.abs(halfW)
        }]);

        path = [M, xPos, lowPos, L, xPos, highPos,
          M, xPos, yPos, L, (xPos + halfW), yPos,
          M, xPos, previousYPos, L, (xPos - halfW), previousYPos
        ];
        setElem = graphics.element;
        setElem = animationManager.setAnimation({
          el: setElem || 'path',
          container: container,
          attr: {
            'path': path,
            'cursor': setLink ? POINTER : BLANKSTRING,
            fill: toRaphaelColor(config.color),
            stroke: toRaphaelColor(config.borderColor),
            'stroke-width': conf.linethickness,
            'stroke-dasharray': config.dashStyle,
            'stroke-linecap': ROUND,
            'stroke-linejoin': ROUND,
            'shape-rendering': 'crisp',
            'visibility': visibleStr
          },
          label: 'path',
          component: dataset,
          callback: applyShadow.call(setElem, config.shadow)
        });
        setElem.data('eventArgs', set.trackerConfig.eventArgs);
        graphics.element = setElem;
      }
    }
  }
  /**
   * parse tooltext from the dataset json
   * @param {number} i index of data
   * @return {string} tooltext
   */
  _parseToolText (i) {
    return __parseToolText.call(this, i);
  }
  /**
   * This method handles all mouse events of an dataset.
   * @param {string} eventType    name of the event
   * @param {number} plotIndex    index of the plot where this event has been occured
   * @param {Event}  e            reference of the original mouse event
   */
  _firePlotEvent (eventType, plotIndex, e) {
    _firePlotEvent.call(this, eventType, plotIndex, e);
  }
  /**
   * function to check if given x,y coordiante is over a column plot or not
   */
  // eslint-disable-next-line
  _checkPointerOverColumn () {
  }
  /**
   * Function that retunr the nearest plot details
   * @param {number} chartX x-axis position of the mouse cordinate
   * @param {number} chartY y-axis position of the mouse cordinate
   * @return {Object} return an object with details of nearest polt and whether it is hovered or not
   */
  _getHoveredPlot (chartX, chartY) {
    var dataset = this,
      x,
      i,
      hoveredInfo,
      pXs,
      pX;

    x = dataset.getFromEnv('xAxis').getValue(chartX);
    pXs = getPlotIndices.call(dataset, x);

    // Checking for overlap between two cosecutive column plots along x-axis
    for (i = pXs.length - 1; i > -1; i--) {
      pX = pXs[i];
      hoveredInfo = (pX - x > 0 ? dataset._checkPointerOverPlot(pX, chartX, chartY) ||
      dataset._checkPointerOverPlot(pX - 1, chartX, chartY)
        : dataset._checkPointerOverPlot(pX + 1, chartX, chartY) ||
            dataset._checkPointerOverPlot(pX, chartX, chartY));
      if (hoveredInfo) {
        break;
      }
    }
    return hoveredInfo;
  }
  /**
   * Helper function of _getHoverPlot fn
   * @param {number} pX     x index value
   * @param {number} chartX x-axis position of the mouse cordinate
   * @param {number} chartY y-axis position of the mouse cordinate
   * @return {Object}       return hover information
   */
  _checkPointerOverPlot (pX, chartX, chartY) {
    return _checkPointerOverPlot.call(this, pX, chartX, chartY);
  }
  /**
   * Helper function of _checkPointerOverPlot fn
   * @param {number} pX     x index value
   * @param {number} chartX x-axis position of the mouse cordinate
   * @param {number} chartY y-axis position of the mouse cordinate
   * @return {Object}       return hover information
   */
  _checkPointerOverErrorBar (pX, chartX, chartY) {
    return _checkPointerOverErrorBar.call(this, pX, chartX, chartY);
  }
  /**
    * This function overrides drawlabel method
    * it prevents the label drawing
    */
  // eslint-disable-next-line
  drawLabel () {
  }
  /**
   * Function to get the maximum and minimum from dataset
   * @return {Object}     contains max, min value
   */
  getDataLimits () {
    var conf = this.config;
    return {
      max: conf.yMax,
      min: conf.yMin,
      xMax: conf.xMax,
      xMin: conf.xMin
    };
  }
  /**
   * Returns the name of the component
   *
   * @return {string} The name of the component
   */
  getName () {
    return 'candlestickbar';
  }
}

export default CandleStickBar;
